import { IdeationVoteEditorVMConfig } from 'components/editors/thread/text/ideationVote/ideationVote';
import { ThreadEditorVMConfig } from 'components/editors/thread/thread.interfaces';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { Person } from 'models/person';
import { TopicThread } from 'models/topicThread';
import { PersonAvatarVMConfig } from '../../avatar/person/person';
import CommentToDoManager from './managers/todo';
import './reply.html';
import './reply.scss';
import CommentRequestButtonVM from './requestButton';

const { discussionId, now } = settings;

components.preload('reply-editor-inline', 'ideation-vote-editor-inline', 'vote-bubble', 'person-avatar');

export interface CommentReplyVMConfig {
    reply: SubscribableOrNullableValue<TopicThread>
    hideControls: SubscribableOrNullableValue<boolean>
    votesPickerContainer: SubscribableOrNullableValue<HTMLElement>
}

@injectable()
export default class CommentReplyVM {
    private isModerator: Subscribable<boolean>

    labels = labels

    reply: Subscribable<TopicThread | undefined>

    replyEditorConfig: ThreadEditorVMConfig
    voteEditorConfig: IdeationVoteEditorVMConfig
    avatarConfig: PersonAvatarVMConfig

    buttons: ObservableArray<CommentRequestButtonVM> = ko.observableArray();

    content: Subscribable<string>
    creatorName: Subscribable<string | undefined>
    whisperText: Subscribable<string>
    createDate: Subscribable<string | undefined>

    vote: Subscribable<number>
    hasVote: Subscribable<boolean>

    isWhisper: Subscribable<boolean>
    isInToDoList: Subscribable<boolean>
    isAbuse: Subscribable<boolean>
    hideControls: Subscribable<boolean>
    
    bodyShown: Subscribable<boolean>
    replyEditorShown: Subscribable<boolean>
    voteEditorShown: Subscribable<boolean>

    busy = ko.pureComputed(() => this.buttons().some(b => b.isBusy()));

    constructor(
        @inject(wccModules.componentConfig) config: CommentReplyVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.reply = ko.flattenComputed(config.reply);

        const isDeleted = this.reply.pluck(r => r.isDeleted, false);
        const isEditing = ko.pureComputed({
            read: () => this.reply()?.editing() ?? false,
            write: value => this.reply()?.editing(value)
        });

        const hideControls = ko.flattenComputed(config.hideControls, false);
        this.hideControls = ko.pureComputed(() => hideControls() || isDeleted());

        const createDate = this.reply.pluck(reply => reply.createDate);

        const participant = this.reply.mapNotNull(reply => Person.fromThread(reply));
        const isModeratorReply = participant.pluck(participant => participant.isModerator, false);
        const isObserverReply = participant.pluck(participant => participant.isObserver, false);

        const currentUserManager = storage.get(CurrentUserManager, { discussionId });
        const currentUser = currentUserManager.pluck(m => m.person);
        this.isModerator = currentUser.pluck(u => u.isModerator, false);
        const isObserver = currentUser.pluck(u => u.isObserver, false);

        this.content = this.reply.pluck(r => r.content, '');
        this.creatorName = participant.pluck(p => p.nicknameOrName);
        this.createDate = createDate.mapNotNull(date => moment(date).locale(settings.languageCode).from(now()));

        this.vote = this.reply.pluck(r => r.vote, 0);
        this.hasVote = this.vote.is(v => v != 0);

        const visibleToFullName = this.reply.pluck(reply => reply.visibleToFullName);

        this.whisperText = ko.pureComputed(() => {
            if (this.isModerator() || isObserver())
                return '@' + (visibleToFullName() ?? labels.ModeratorsAndObservers);
            else
                return WCC.Labels.Private;
        });

        this.isWhisper = this.reply.pluck('isWhisper', false);
        this.isAbuse = ko.pureComputed(() => (this.reply()?.spamComplaintCount() ?? 0) > 0 && this.isModerator());

        const todoManager = new CommentToDoManager(this.reply, storage);
        this.isInToDoList = todoManager.isInToDoList;
        const isInToDoListOrReported = todoManager.isInToDoListOrReported;        

        this.bodyShown = ko.pureComputed(() => !isEditing());
        this.replyEditorShown = ko.pureComputed(() => isEditing() && !this.hasVote());
        this.voteEditorShown = ko.pureComputed(() => isEditing() && this.hasVote());

        this.replyEditorConfig = {
            thread: this.reply,
            onCancel: () => isEditing(false),
            onSave: () => isEditing(false)
        }

        this.voteEditorConfig = {
            thread: this.reply,
            votesPickerContainer: config.votesPickerContainer,
            onCancel: () => isEditing(false),
            onSave: () => isEditing(false)
        }

        this.avatarConfig = { person: participant }

        ko.applyBindingsToNode(info.element, {
            'css': {
                'comment__reply': this,
                'comment__reply--moderator': isModeratorReply,
                'comment__reply--observer': isObserverReply,
                'comment__reply--abuse': this.isAbuse,
                'comment__reply--todo': isInToDoListOrReported,
                'comment__reply--editing': isEditing
            }
        }, this);
    }
}